// Rudras-AI Stripe Terminal Payment Dialog — Reader M2 (Sep 2026).
//
// Kiosk-side component that walks a customer through a card-present
// payment on the Stripe Reader M2. Reuses the shadcn/ui + toast
// conventions already established in `components/retail/customer/`.
//
// What this component is NOT
// --------------------------
// It does not talk to the reader. It cannot: the Reader M2 is a
// Bluetooth device driven by the Stripe Terminal ANDROID SDK, and this
// is a browser page. The physical card interaction happens entirely in
// the companion Rudras Stripe Terminal agent app running on the same
// Samsung Galaxy Tab S8; the two are coupled only through the Rudras
// backend. See `agent-stripe-terminal-android/README.md`.
//
// So this dialog's whole job is: start a server-side session, tell the
// customer what to do at the reader, and poll until the SERVER says the
// payment reached a terminal state. It never decides that a payment
// succeeded — a frontend success message is explicitly not allowed to
// release an order to the kitchen.
//
// Integration into CheckoutModal.jsx:
//   1. `GET /api/retail/public/{slug}/pos/stripe-terminal-status` on
//      mount decides whether "Pay at Reader" is offered at all.
//   2. On submit with `paymentMethod === "stripe_terminal"`, the order
//      is created as usual, then this dialog is mounted.
//   3. This dialog owns the rest of the lifecycle and reports the
//      outcome through `onDone`.

import React from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../ui/dialog";
import { Button } from "../../ui/button";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Statuses from `payments/stripe_terminal/repository.public_view`.
const IN_FLIGHT = new Set([
  "created",
  "awaiting_device",
  "claimed",
  "payment_in_progress",
]);
const TERMINAL_SUCCESS = "succeeded";
const TERMINAL_DECLINE = "declined";
const TERMINAL_CANCEL = "cancelled";
const TERMINAL_EXPIRED = "expired";
const TERMINAL_FAILED = "failed_terminal";
const TERMINAL_RECON = "reconciliation_required";

const POLL_MS = 2000;

/**
 * Props:
 *   slug            — tenant slug (routes are public + per-order gated).
 *   orderId         — Rudras retail_orders.id of an already-placed order.
 *   orderAccessToken— the per-order `X-Order-Access` credential minted at
 *                     checkout. Required: these routes authorize a real
 *                     charge and `order_code` is not a secret.
 *   open            — controlled visibility.
 *   onSession(s)    — called whenever the session view changes, so the
 *                     parent can persist its recovery pointer.
 *   resumeSessionId — resume an existing session after a browser restart.
 *   onDone(status, session) — called ONCE on a terminal state. `status`
 *                     is "paid" | "declined" | "cancelled" | "expired" |
 *                     "reconciliation" | "failed".
 */
// Codes that mean the resumed session can NEVER resolve, so a stored
// recovery pointer to it is worthless and must be released. The backend
// returns `detail` either as a bare string (`"order_not_found"`, from
// tenant/order resolution) or as an object (`{code, message}`, from
// `StripeTerminalError`) — both shapes are read here.
const DEAD_SESSION_CODES = new Set([
  "order_not_found",
  "stripe_terminal_session_not_found",
]);

export function isDeadSessionResponse(err) {
  if (err?.response?.status !== 404) return false;
  const det = err?.response?.data?.detail;
  const code = typeof det === "string" ? det : det?.code;
  return DEAD_SESSION_CODES.has(code);
}

export default function StripeTerminalPaymentDialog({
  slug,
  orderId,
  orderAccessToken,
  open,
  onDone,
  onSession,
  resumeSessionId,
}) {
  const [session, setSession] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [starting, setStarting] = React.useState(true);
  const timerRef = React.useRef(null);
  const doneRef = React.useRef(false);

  const client = React.useMemo(
    () =>
      axios.create({
        baseURL: API_BASE,
        headers: orderAccessToken
          ? { "X-Order-Access": orderAccessToken }
          : {},
      }),
    [orderAccessToken]
  );

  const base = `/api/retail/public/${slug}/stripe-terminal/sessions`;

  // Kick off (or resume) the session exactly once per open.
  React.useEffect(() => {
    if (!open || !orderId) return;
    let cancelled = false;
    (async () => {
      try {
        setStarting(true);
        if (resumeSessionId) {
          // Browser-restart recovery: never re-initiate, which would
          // authorize a second charge. Resume polling the existing row.
          const { data } = await client.get(`${base}/${resumeSessionId}`);
          if (cancelled) return;
          setSession(data.session);
          onSession?.(data.session);
          return;
        }
        const { data } = await client.post(base, { order_id: orderId });
        if (cancelled) return;
        setSession(data.session);
        onSession?.(data.session);
      } catch (e) {
        if (cancelled) return;
        const det = e?.response?.data?.detail;
        // Sep 2026 — stale-recovery-pointer self-healing.
        //
        // A RESUME that 404s means the pointed-at session is
        // definitively gone: the order was purged, the session swept,
        // or the pointer belongs to a decommissioned environment. The
        // old code only rendered an error, and because it never called
        // `onDone`, the parent's `CLEAR_ON` rule never ran — so the
        // pointer survived its full 24h TTL and every reload re-opened
        // the dialog against the same dead session. The customer was
        // locked out of Pay at Reader with no self-service recovery,
        // and support had to clear browser storage by hand.
        //
        // Report it through the EXISTING lifecycle as `expired`, which
        // the parent already treats as "clear the pointer", so the next
        // attempt starts a fresh session.
        //
        // Deliberately narrow — this fires ONLY when all three hold:
        //   1. we were RESUMING (a failed brand-new POST must not be
        //      mistaken for a dead pointer: there is nothing to clear,
        //      and the customer should simply retry);
        //   2. the server answered 404 (not a timeout, not a network
        //      drop, not a 5xx — those may be transient and the reader
        //      may already have taken the card);
        //   3. the code is one of the two that mean "this session can
        //      never resolve".
        // Every other failure keeps the pointer, because a customer who
        // might already have been charged must stay recoverable.
        if (resumeSessionId && isDeadSessionResponse(e)) {
          if (!doneRef.current) {
            doneRef.current = true;
            onDone?.("expired", null);
          }
          return;
        }
        setError(
          (det && (det.message || det.code)) ||
            "Couldn't start the card payment. Please retry or ask staff for help."
        );
      } finally {
        if (!cancelled) setStarting(false);
      }
    })();
    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, orderId, resumeSessionId, client]);

  // Poll while in flight. The SERVER is the sole authority on the
  // outcome — this loop reads it, it never infers it.
  React.useEffect(() => {
    if (!session?.session_id) return;
    if (!IN_FLIGHT.has(session.status)) {
      if (doneRef.current) return;
      doneRef.current = true;
      onDone?.(mapTerminal(session.status), session);
      return;
    }
    timerRef.current = setTimeout(async () => {
      try {
        const { data } = await client.get(`${base}/${session.session_id}`);
        setSession(data.session);
        onSession?.(data.session);
      } catch (_e) {
        // Transient network error — keep polling. A dropped poll must
        // never be reported to the customer as a failed payment; the
        // reader may well have already succeeded, and the backend
        // webhook will settle it either way.
      }
    }, POLL_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, client]);

  // Sep 2026 — cancellable for as long as the payment is in flight,
  // including once the reader is collecting. Before a reader picks the
  // session up the backend closes it at once; after that it ASKS the
  // reader's agent to stop and answers 202, and this dialog keeps
  // polling until the server reports what actually happened. A card the
  // reader already accepted still completes — the server, not this
  // button, decides.
  const [cancelling, setCancelling] = React.useState(false);
  const cancelPending = !!session?.cancel_requested;
  const canCancel =
    !!session && IN_FLIGHT.has(session.status) && !cancelPending && !cancelling;

  const onCancel = async () => {
    if (!session?.session_id || cancelling) return;
    setCancelling(true);
    try {
      const { data } = await client.post(`${base}/${session.session_id}/cancel`);
      if (data?.cancel_requested) {
        // The reader has been asked to stop. The poll above reports the
        // outcome; the dialog stays open until it does.
        if (data.session) setSession(data.session);
        return;
      }
      // Not a pending request: the server has already decided. Report
      // what it decided — never assume "cancelled" (an attempt the server
      // could not prove uncollected is `reconciliation_required`).
      const decided = data?.session || session;
      if (!doneRef.current && !IN_FLIGHT.has(decided?.status)) {
        doneRef.current = true;
        onDone?.(mapTerminal(decided?.status), decided);
      } else if (data?.session) {
        setSession(data.session);
      }
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      toast.error(
        code === "stripe_terminal_session_not_cancellable"
          ? "This payment has already completed or is being verified, so it can no longer be cancelled."
          : "Payment could not be cancelled. Please try again or ask staff for help."
      );
    } finally {
      setCancelling(false);
    }
  };

  return (
    <Dialog open={open} data-testid="stripe-terminal-dialog">
      {/* No corner X: hiding this dialog would not stop the reader, so the
          only exits are "Cancel Payment" or an outcome from the server. */}
      <DialogContent className="max-w-md" hideClose>
        <DialogHeader>
          <DialogTitle>Card Payment</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2" data-testid="stripe-terminal-body">
          <StateView
            session={session}
            error={error}
            starting={starting}
            cancelling={cancelling || cancelPending}
          />
        </div>
        <DialogFooter>
          {(canCancel || cancelling) && (
            <Button
              variant="outline"
              onClick={onCancel}
              disabled={cancelling}
              data-testid="stripe-terminal-cancel-btn"
            >
              {cancelling ? "Cancelling…" : "Cancel Payment"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StateView({ session, error, starting, cancelling }) {
  if (error) {
    return (
      <div data-testid="stripe-terminal-error" className="text-sm text-red-700">
        {error}
      </div>
    );
  }
  if (starting || !session) {
    return (
      <Row
        icon={<Spinner />}
        label="Preparing payment…"
        testId="stripe-terminal-preparing"
      />
    );
  }
  if (cancelling && IN_FLIGHT.has(session.status)) {
    return (
      <Row
        icon={<Spinner />}
        label="Cancelling payment…"
        testId="stripe-terminal-cancelling"
      />
    );
  }
  switch (session.status) {
    case "created":
    case "awaiting_device":
    // A reader holding the session is still WAITING for the card until
    // the customer presents one; nothing in the session says a card has
    // been read, so the customer is told what to do, not "processing".
    case "claimed":
    case "payment_in_progress":
      // The three card-entry methods the Reader M2 supports, named
      // explicitly so the customer knows all of them are available.
      return (
        <Row
          icon={<Spinner />}
          label="Tap, insert, or swipe your card on the reader."
          testId="stripe-terminal-awaiting"
        />
      );
    case TERMINAL_SUCCESS:
      return (
        <Row
          icon={<Dot color="green" />}
          label={
            "Paid." +
            (session.card_brand && session.card_last4
              ? ` ${session.card_brand.toUpperCase()} ••••${session.card_last4}`
              : "")
          }
          testId="stripe-terminal-paid"
        />
      );
    case TERMINAL_DECLINE:
      return (
        <Row
          icon={<Dot color="red" />}
          label={
            "Card was declined." +
            (session.error_code ? ` (${session.error_code})` : "")
          }
          testId="stripe-terminal-declined"
        />
      );
    case TERMINAL_CANCEL:
      return (
        <Row
          icon={<Dot color="gray" />}
          label="Payment cancelled."
          testId="stripe-terminal-cancelled"
        />
      );
    case TERMINAL_EXPIRED:
      return (
        <Row
          icon={<Dot color="orange" />}
          label="Payment timed out. Please start a new payment."
          testId="stripe-terminal-expired"
        />
      );
    case TERMINAL_FAILED:
      return (
        <Row
          icon={<Dot color="red" />}
          label={
            "Payment could not complete." +
            (session.error_code ? ` (${session.error_code})` : "")
          }
          testId="stripe-terminal-failed"
        />
      );
    case TERMINAL_RECON:
      // Deliberately does NOT tell the customer whether they were
      // charged — the backend genuinely does not know yet, and guessing
      // either way is worse than asking for staff.
      return (
        <Row
          icon={<Dot color="orange" />}
          label="This payment needs staff assistance. Please ask an employee before paying again."
          testId="stripe-terminal-reconciliation"
        />
      );
    default:
      return (
        <Row
          icon={<Spinner />}
          label={`Status: ${session.status}`}
          testId="stripe-terminal-unknown"
        />
      );
  }
}

function Row({ icon, label, testId }) {
  return (
    <div className="flex items-center gap-3" data-testid={testId}>
      <span>{icon}</span>
      <span className="text-sm">{label}</span>
    </div>
  );
}

function Spinner() {
  return <Loader2 className="h-5 w-5 animate-spin text-indigo-500" />;
}

function Dot({ color }) {
  const cls = {
    green: "bg-emerald-500",
    red: "bg-red-500",
    orange: "bg-orange-500",
    gray: "bg-slate-400",
  }[color];
  return <span className={"inline-block h-3 w-3 rounded-full " + cls} />;
}

export function mapTerminal(status) {
  switch (status) {
    case TERMINAL_SUCCESS:
      return "paid";
    case TERMINAL_DECLINE:
      return "declined";
    case TERMINAL_CANCEL:
      return "cancelled";
    case TERMINAL_EXPIRED:
      return "expired";
    case TERMINAL_RECON:
      return "reconciliation";
    default:
      return "failed";
  }
}
